import moment from "moment";

export const TAMANHO_MAXIMO_ATO = 500;
export const TAMANHO_MAXIMO_OBSERVACAO = 500;

// Converte "52,26" (pt-BR) em número. Valor vazio ou inválido vira 0.
export const converterParaNumero = (valor) => {
    if (valor === null || valor === undefined || valor === "") return 0;
    if (typeof valor === "number") return valor;
    const numero = parseFloat(valor.toString().replace(",", "."));
    return isNaN(numero) ? 0 : numero;
};

// Formata número como "52,26".
export const formatarValor = (valor) => converterParaNumero(valor).toFixed(2).replace(".", ",");

// Máscara do fator de correção: 4 casas decimais ("0,0452" = 4,52%).
export const fatorMask = (valor) => {
    const digitos = (valor ?? "").toString().replace(/\D/g, "");
    return (parseFloat(digitos || "0") / 10000).toFixed(4).replace(".", ",");
};

// valor + valor × fator, arredondado em 2 casas (mesma regra do backend).
export const calcularValorReajustado = (valor, fator) => {
    const base = converterParaNumero(valor);
    return Math.round(base * (1 + converterParaNumero(fator)) * 100 + 1e-9) / 100;
};

// Sugestão de vigência: 1º de fevereiro até 31 de janeiro do ano seguinte (portaria anual de janeiro).
export const sugerirVigencia = (hoje = moment()) => {
    let inicio = moment(hoje).year(hoje.year()).month(1).date(1);
    if (inicio.isBefore(hoje, "day")) inicio = inicio.add(1, "year");

    return {
        inicioVigencia: inicio.format("YYYY-MM-DD"),
        fimVigencia: moment(inicio).add(1, "year").month(0).date(31).format("YYYY-MM-DD")
    };
};

// Valida os dados do cabeçalho do reajuste espelhando as regras do backend. Retorna a mensagem ou null.
export const validarDadosReajuste = ({ atoAdministrativo, inicioVigencia, fimVigencia, fatorCorrecao, observacao }, hoje = moment()) => {
    if (!atoAdministrativo || !atoAdministrativo.trim())
        return "Informe o ato administrativo.";

    if (atoAdministrativo.trim().length > TAMANHO_MAXIMO_ATO)
        return `O ato administrativo deve ter no máximo ${TAMANHO_MAXIMO_ATO} caracteres.`;

    if (!observacao || !observacao.trim())
        return "Informe a observação referente à normativa que estabeleceu o índice de atualização.";

    if (observacao.trim().length > TAMANHO_MAXIMO_OBSERVACAO)
        return `A observação deve ter no máximo ${TAMANHO_MAXIMO_OBSERVACAO} caracteres.`;

    if (!inicioVigencia || !fimVigencia)
        return "Informe o início e o fim da vigência.";

    if (moment(inicioVigencia).isBefore(hoje, "day"))
        return "A data de início da vigência não pode ser anterior a hoje.";

    if (!moment(fimVigencia).isAfter(moment(inicioVigencia), "day"))
        return "A data de término da vigência deve ser posterior ao início.";

    if (converterParaNumero(fatorCorrecao) <= 0)
        return "Informe um fator de correção maior que zero.";

    return null;
};

// Monta a prévia: só infrações ativas com valor a reajustar (valor > 0).
export const montarPrevia = (infracoes, fatorCorrecao, aplicarReincidencia) => {
    return (infracoes ?? [])
        .filter(infracao => infracao.status === "Ativo" && converterParaNumero(infracao.valor) > 0)
        .map(infracao => {
            const reincidenciaAnterior = converterParaNumero(infracao.valorReincidencia);
            const temReincidencia = aplicarReincidencia && reincidenciaAnterior > 0;

            return {
                id: infracao.id,
                identificacao: infracao.identificacao,
                valorAnterior: formatarValor(infracao.valor),
                valorNovo: formatarValor(calcularValorReajustado(infracao.valor, fatorCorrecao)),
                valorReincidenciaAnterior: temReincidencia ? formatarValor(reincidenciaAnterior) : null,
                valorReincidenciaNovo: temReincidencia
                    ? formatarValor(calcularValorReajustado(reincidenciaAnterior, fatorCorrecao))
                    : null
            };
        });
};

// Valida os valores digitados na prévia. Retorna a mensagem ou null.
export const validarPrevia = (previa) => {
    if (!previa || previa.length === 0)
        return "Não há infrações ativas com valor para reajustar.";

    const invalida = previa.find(item =>
        converterParaNumero(item.valorNovo) <= 0 ||
        (item.valorReincidenciaNovo !== null && converterParaNumero(item.valorReincidenciaNovo) <= 0));

    return invalida ? `Informe valores maiores que zero para a infração "${invalida.identificacao}".` : null;
};

// Monta o corpo enviado a Infracao/AgendarReajuste.
export const montarPayloadReajuste = (tipoFiscalizacao_Id, dados, previa) => ({
    tipoFiscalizacao_Id,
    atoAdministrativo: dados.atoAdministrativo.trim(),
    inicioVigencia: dados.inicioVigencia,
    fimVigencia: dados.fimVigencia,
    fatorCorrecao: converterParaNumero(dados.fatorCorrecao),
    aplicarReincidencia: dados.aplicarReincidencia,
    observacao: dados.observacao.trim(),
    itens: previa.map(item => ({
        infracao_Id: item.id,
        valorNovo: converterParaNumero(item.valorNovo),
        valorReincidenciaNovo: item.valorReincidenciaNovo === null ? null : converterParaNumero(item.valorReincidenciaNovo)
    }))
});
